import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getVisibleEvents } from "../../firebase";
import { Event } from "../../types";
import { format } from "date-fns";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const formatDate = (dateString: string) => {
  try {
    const parts = dateString.split("-");
    return format(
      new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2])),
      "MMMM d, yyyy"
    );
  } catch {
    return dateString;
  }
};

const stripHtml = (html: string) => {
  const temp = document.createElement("div");
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || "";
};

const EventDetailDialog: React.FC<{
  event: Event | null;
  open: boolean;
  onClose: () => void;
}> = ({ event, open, onClose }) => {
  if (!event) return null;

  return (
    <div
      className={`fixed inset-0 z-50 overflow-y-auto ${
        open ? "block" : "hidden"
      }`}
    >
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
          &#8203;
        </span>
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-start">
              <h3 className="text-2xl font-bold text-gray-900">
                {event.title}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {event.imageURL && (
              <div className="mt-4 mb-6 rounded-lg overflow-hidden">
                <img
                  src={event.imageURL}
                  alt={event.title}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}

            <div className="gap-2 mb-6">
              <div className="flex items-center px-3 py-1">
                <CalendarIcon className="h-4 w-4 mr-1" />
                <span>{formatDate(event.date)}</span>
              </div>

              {event.time && (
                <div className="flex items-center px-3 py-1">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  <span>{event.time}</span>
                </div>
              )}

              {event.location && (
                <div className="mb-6 px-3 py-1">
                  <div className="flex items-center text-gray-700">
                    <MapPinIcon className="h-5 w-5 mr-2" />
                    <span>{event.location}</span>
                  </div>

                  {event.locationURL && (
                    <a
                      href={event.locationURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <MapPinIcon className="h-4 w-4 mr-2" />
                      View on Google Maps
                    </a>
                  )}
                </div>
              )}
            </div>

            <div
              className="prose max-w-none"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          </div>

          <div className="flex justify-between p-4">
            {event.price && (
              <div className="flex items-center px-3 py-1 text-lg font-semibold">
                <span>$ {event.price}</span>
              </div>
            )}

            <div
              className={`px-3 py-1 rounded-full ${
                event.status === "open"
                  ? "bg-green-100 text-green-800"
                  : event.status === "closed"
                  ? "bg-red-100 text-red-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {event.status === "open"
                ? "Open"
                : event.status === "closed"
                ? "Closed"
                : "Hidden"}
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse sm:justify-between gap-3 space-y-3 sm:space-y-0">
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:ml-3 sm:w-auto sm:text-sm"
            >
              Buy Ticket
            </button>
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gray-200 text-base font-medium text-gray-500 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Events: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Determine if we're in the portal or public route
  const isPortalRoute = location.pathname.startsWith("/portal");

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const data = await getVisibleEvents();
        setEvents(data);
      } catch (err: any) {
        console.error("Error loading events:", err);
        setError("Failed to load events. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleOpenEvent = (event: Event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleRegisterClick = (event: Event, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent opening the event details dialog

    // If we're in the portal, use the portal route, otherwise use the public route
    if (isPortalRoute) {
      navigate(`/portal/events/registration/${event.id}`);
    } else {
      navigate(`/events/registration/${event.id}`);
    }
  };

  return (
    <div className="container mx-auto py-8">
      {loading ? (
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
        </div>
      ) : error ? (
        <div className="text-center text-red-600">{error}</div>
      ) : events.length === 0 ? (
        <div className="text-center py-10">
          <CalendarIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-medium text-gray-900">No events found</h3>
          <p className="text-gray-500 mt-2">
            Check back later for upcoming events.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {events.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-200 cursor-pointer flex flex-col h-full"
              onClick={() => handleOpenEvent(event)}
            >
              {event.imageURL ? (
                <img
                  src={event.imageURL}
                  alt={event.title}
                  className="w-full aspect-square object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gold flex items-center justify-center">
                  <CalendarIcon className="h-12 w-12 text-white" />
                </div>
              )}
              <div className="p-4 flex-grow">
                <h3 className="font-bold text-xl mb-2">{event.title}</h3>
                <p className="text-gray-700 mb-4 line-clamp-2">
                  {stripHtml(event.description)}
                </p>
                <div className="flex items-center text-gray-600 mb-2">
                  <CalendarIcon className="h-5 w-5 mr-2" />
                  <span>{formatDate(event.date)}</span>
                </div>
                <div className="flex items-center text-gray-600 mb-2">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  <span>{event.time}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPinIcon className="h-5 w-5 mr-2" />
                  <span>{event.location}</span>
                </div>
              </div>
              <div className="px-4 pb-4 mt-2">
                <div className="flex justify-between items-center">
                  <div className="font-bold text-lg">
                    {parseFloat(event.price) > 0
                      ? `$${parseFloat(event.price).toFixed(2)}`
                      : "Free"}
                  </div>
                  {event.status === "open" && (
                    <button
                      className="bg-gold hover:bg-gold text-white font-semibold px-4 py-2 rounded-xl"
                      onClick={(e) => handleRegisterClick(event, e)}
                    >
                      Register
                    </button>
                  )}
                  {event.status === "closed" && (
                    <span className="text-red-500 font-medium">
                      Registration Closed
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Event Detail Dialog */}
      {dialogOpen && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col sm:max-h-[90vh] max-h-screen sm:rounded-lg rounded-none sm:max-w-3xl max-w-none">
            <div className="flex justify-between items-center p-4 border-b flex-shrink-0">
              <h2 className="text-2xl font-bold">{selectedEvent.title}</h2>
              <button
                onClick={handleCloseDialog}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {selectedEvent.imageURL && (
              <div className="flex-shrink-0">
                <img
                  src={selectedEvent.imageURL}
                  alt={selectedEvent.title}
                  className="w-full h-64 object-cover"
                />
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 mr-2 text-gold" />
                  <span>{formatDate(selectedEvent.date)}</span>
                </div>
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2 text-gold" />
                  <span>{selectedEvent.time}</span>
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 mr-2 text-gold" />
                  <span className="flex-1">{selectedEvent.location}</span>
                </div>
                <div className="flex items-center">
                  <span className="font-bold mr-2">Price:</span>
                  <span>
                    {parseFloat(selectedEvent.price) > 0
                      ? `$${parseFloat(selectedEvent.price).toFixed(2)}`
                      : "Free"}
                  </span>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">About this event</h3>
                <div
                  className="prose max-w-none"
                  dangerouslySetInnerHTML={{
                    __html: selectedEvent.description,
                  }}
                />
              </div>

              {selectedEvent.locationURL && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-2">Location</h3>
                  <a
                    href={selectedEvent.locationURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline"
                  >
                    View on Google Maps
                  </a>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                {selectedEvent.status === "open" ? (
                  <button
                    className="bg-gold hover:bg-blue-600 text-white px-6 py-2 rounded"
                    onClick={(e) => {
                      handleCloseDialog();
                      // Use the appropriate route based on where we are
                      if (isPortalRoute) {
                        navigate(
                          `/portal/events/registration/${selectedEvent.id}`
                        );
                      } else {
                        navigate(`/events/registration/${selectedEvent.id}`);
                      }
                    }}
                  >
                    Register Now
                  </button>
                ) : (
                  <span className="bg-red-100 text-red-800 px-4 py-2 rounded">
                    Registration Closed
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
